/**
 * Basic Report Component
 * Displays main metrics comparison for PROFESSIONAL tier subscribers
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { getMetricAtWeek } from '../../../lib/services/compReportService';

const BasicReport = ({ reportData, loading, error }) => {
  // Main metrics to display
  const mainMetrics = [
    { key: 'pctAware', label: 'Awareness', unit: '%' },
    { key: 'pctInterest', label: 'Interest', unit: '%' },
    { key: 'pctTheater', label: 'Theater Intent', unit: '%' },
    { key: 'pctWillingToPay', label: 'Willing to Pay', unit: '%' },
    { key: 'pctHeat', label: 'Heat', unit: '%' },
    { key: 'pctPLF', label: 'PLF', unit: '%' }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error.message || 'Failed to load report data'}
      </Alert>
    );
  }

  if (!reportData || !reportData.primaryFilm) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          No data available for comparison. Ensure the primary film and comp films have survey data.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Check the browser console for detailed error messages.
        </Typography>
      </Paper>
    );
  }

  if (!reportData.weeks || reportData.weeks.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          No survey data found aligned by weeks before release.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Primary film has data: {Object.keys(reportData.primaryFilm.dataByWeeks || {}).length > 0 ? 'Yes' : 'No'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Check the browser console for detailed logging about the data fetch.
        </Typography>
      </Paper>
    );
  }

  const { primaryFilm, compFilms, weeks } = reportData;

  // Get color for metric value
  const getMetricColor = (value) => {
    if (value === 'N/A' || typeof value !== 'number') return 'text.secondary';
    if (value >= 50) return 'success.main';
    if (value >= 30) return 'warning.main';
    return 'error.main';
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Basic Comparison Report
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Comparing {primaryFilm.filmTitle} with {compFilms.length} comp film{compFilms.length !== 1 ? 's' : ''} by weeks before release
      </Typography>

      {mainMetrics.map((metric, metricIndex) => (
        <TableContainer component={Paper} key={metric.key} sx={{ mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {metric.label}
            </Typography>
          </Box>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
                  Weeks Before Release
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                  {primaryFilm.filmTitle}
                </TableCell>
                {compFilms.map((comp, index) => (
                  <TableCell key={comp.imdbId} sx={{ fontWeight: 600 }}>
                    {comp.filmTitle}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {weeks.map((week) => {
                const primaryValue = getMetricAtWeek(primaryFilm, week, metric.key);
                return (
                  <TableRow key={week} hover>
                    <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
                      {week} weeks
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        backgroundColor: 'primary.light',
                        color: typeof primaryValue === 'number' ? getMetricColor(primaryValue) : 'text.secondary',
                        fontWeight: 600
                      }}
                    >
                      {primaryValue === 'N/A' ? (
                        <Chip label="N/A" size="small" variant="outlined" />
                      ) : (
                        `${primaryValue}${metric.unit}`
                      )}
                    </TableCell>
                    {compFilms.map((comp) => {
                      const compValue = getMetricAtWeek(comp, week, metric.key);
                      return (
                        <TableCell 
                          key={comp.imdbId}
                          sx={{ 
                            color: typeof compValue === 'number' ? getMetricColor(compValue) : 'text.secondary'
                          }}
                        >
                          {compValue === 'N/A' ? (
                            <Chip label="N/A" size="small" variant="outlined" />
                          ) : (
                            `${compValue}${metric.unit}`
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ))}
    </Box>
  );
};

export default BasicReport;

